import React, { useState } from "react";
import { CalendarDays, Copy, Bell, ChevronRight, Check, Loader2, PartyPopper } from "lucide-react";
import { papi } from "../../../lib/portalApi";
import { brl, formatDate, formatErr } from "../../../lib/api";
import { PixModal } from "../PixModal";
import { T, track } from "./theme";
import { toast } from "sonner";

const statusOf = (nd) => (nd.overdue ? { label: `Vencida há ${nd.overdue_days} dia${nd.overdue_days > 1 ? "s" : ""}`, fg: "#D32F45", bg: "#FDEEF0" }
  : { label: "Em dia", fg: "#2E7D4F", bg: "#E7F5EC" });

export const NextInstallmentCard = ({ nextDue, onRemind, onPaid }) => {
  const [pix, setPix] = useState(false);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);
  if (!nextDue) {
    return (
      <section className="flex items-center gap-3 rounded-[22px] p-5" style={{ background: T.greenBg }} data-testid="next-installment-clear">
        <PartyPopper size={24} style={{ color: T.green }} />
        <div><b className="block text-[16px]" style={{ color: T.green }}>Tudo em dia!</b>
          <span className="text-[14px]" style={{ color: T.muted }}>Você não tem parcelas pendentes no momento.</span></div>
      </section>
    );
  }
  const it = nextDue.items[0];
  const st = statusOf(nextDue);
  const ref = `${it.bu}:${it.installment_id}`;
  const copyPix = async () => {
    track("copy_pix_click", ref); setCopying(true);
    try {
      const { data } = await papi.post(`/portal/installments/${it.bu}/${it.installment_id}/pix`);
      await navigator.clipboard.writeText(data.copy_paste);
      setCopied(true); setTimeout(() => setCopied(false), 2500);
      toast.success("PIX copia e cola copiado");
    } catch (e) {
      const code = e?.response?.status || 0;
      toast.error((code && code < 500 && formatErr(e?.response?.data?.detail)) || "Não foi possível obter o PIX agora. Tente novamente em instantes.");
    }
    finally { setCopying(false); }
  };
  return (
    <section className="rounded-[22px] bg-white p-5 shadow-[0_14px_30px_-22px_rgba(42,14,79,0.5)]" data-testid="next-installment-card">
      <div className="flex items-start gap-3">
        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl" style={{ background: T.lavender, color: T.violet }}><CalendarDays size={21} /></span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1">
            <span className="text-[13px] font-bold uppercase tracking-wide" style={{ color: T.muted }}>Próxima parcela</span>
            <span className="rounded-full px-2.5 py-0.5 text-[11px] font-bold" style={{ background: st.bg, color: st.fg }} data-testid="next-installment-status">{st.label}</span>
          </div>
          <div className="text-[14px] font-semibold" style={{ color: T.deep }} data-testid="next-installment-due">{nextDue.overdue ? "Venceu em" : "Vence em"} {formatDate(nextDue.due_date)}</div>
          <div className="mt-0.5 text-[12px]" style={{ color: T.muted }}>{it.segment} · compra {it.sale_number} · parcela {it.number}</div>
        </div>
      </div>
      <div className="mt-3 text-[28px] font-extrabold tabular-nums" style={{ color: T.deep }} data-testid="next-installment-amount">{brl(it.updated_total)}</div>
      {(it.fine > 0 || it.interest > 0) && <div className="text-[12px]" style={{ color: T.muted }}>Inclui multa e juros do atraso</div>}
      {nextDue.count > 1 && <div className="mt-1 text-[12px] font-semibold" style={{ color: T.violet }} data-testid="next-installment-more">+ {nextDue.count - 1} parcela{nextDue.count > 2 ? "s" : ""} na mesma data (total {brl(nextDue.total)})</div>}
      <button onClick={() => { track("pay_click", ref); setPix(true); }} className="mt-4 flex w-full items-center justify-center gap-2 rounded-2xl py-3.5 text-[16px] font-extrabold text-white transition-transform active:scale-[0.99]"
        style={{ background: "linear-gradient(135deg, #2E7D4F 0%, #3FA067 100%)" }} data-testid="next-installment-pay-btn">
        Pagar agora <ChevronRight size={18} />
      </button>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <button onClick={copyPix} disabled={copying} className="flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-[14px] font-bold"
          style={{ borderColor: T.line, color: T.violet }} data-testid="next-installment-copy-pix-btn">
          {copying ? <Loader2 size={16} className="animate-spin" /> : copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copiado" : "Copiar PIX"}
        </button>
        <button onClick={() => onRemind(nextDue, it)} className="flex items-center justify-center gap-1.5 rounded-xl border py-2.5 text-[14px] font-bold"
          style={{ borderColor: T.line, color: T.violet }} data-testid="next-installment-remind-btn">
          <Bell size={16} /> Lembrar-me
        </button>
      </div>
      {pix && <PixModal open onClose={() => { setPix(false); onPaid(); }} bu={it.bu} installmentId={it.installment_id} />}
    </section>
  );
};
